import { useEffect, useRef, useState } from 'react';

const ALERT_POPOVER_WIDTH = 240;
const ALERT_POPOVER_GAP = 4;

interface UseAlertPopoverPositionParams {
  targetEl?: HTMLElement;
  alertRect?: DOMRect;
  containerEl?: HTMLElement | null;
}

interface UseAlertPopoverPositionReturn {
  popoverRef: React.RefObject<HTMLDivElement>;
  shouldPositionAbove: boolean;
  shouldPositionLeft: boolean;
  positionStyle: React.CSSProperties;
}

export const useAlertPopoverPosition = ({
  targetEl,
  alertRect,
  containerEl,
}: UseAlertPopoverPositionParams): UseAlertPopoverPositionReturn => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [shouldPositionAbove, setShouldPositionAbove] = useState(false);
  const [shouldPositionLeft, setShouldPositionLeft] = useState(false);

  useEffect(() => {
    if (popoverRef.current && targetEl && alertRect) {
      const inputRect = targetEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const absoluteAlertRect = {
        x: inputRect.left + alertRect.left,
        y: inputRect.top + alertRect.top - (containerEl?.scrollTop || 0),
        height: alertRect.height,
      };

      const spaceBelow =
        viewportHeight - (absoluteAlertRect.y + absoluteAlertRect.height);
      const spaceAbove = absoluteAlertRect.y;
      const spaceToRight = viewportWidth - absoluteAlertRect.x;

      const popoverHeight = popoverRef.current.offsetHeight;
      setShouldPositionAbove(
        spaceBelow < popoverHeight && spaceAbove > spaceBelow,
      );

      setShouldPositionLeft(spaceToRight < ALERT_POPOVER_WIDTH);
    }
  }, [targetEl, alertRect, containerEl]);

  let positionStyle: React.CSSProperties = {};

  if (alertRect && targetEl) {
    const inputRect = targetEl.getBoundingClientRect();
    const scrollOffset = containerEl?.scrollTop || 0;

    positionStyle = {
      position: 'fixed',
      zIndex: 5,
      left: shouldPositionLeft
        ? inputRect.left +
          alertRect.left +
          alertRect.width -
          ALERT_POPOVER_WIDTH
        : inputRect.left + alertRect.left,
      top: shouldPositionAbove
        ? inputRect.top +
          alertRect.top -
          scrollOffset -
          (popoverRef.current ? popoverRef.current.offsetHeight : 0)
        : inputRect.top +
          alertRect.top +
          alertRect.height -
          scrollOffset +
          ALERT_POPOVER_GAP,
    };
  }

  return {
    popoverRef,
    shouldPositionAbove,
    shouldPositionLeft,
    positionStyle,
  };
};
